import { RecordingState, initialState } from './reducer';
import { RootState } from '../../store/rootReducer';
import {
    RecorderStatus,
    ModuleStatus,
} from './types';

export enum RecordStatusCaption{
    RECORDING = 'recording',
    SAVING = 'saving',
    PAUSED = 'paused',
    CANCELLING = 'cancelling',
    RESTARTING = 'restarting',
    DEFAULT_STATE = 'defaultStatus'
}

const isModulePaused = (state: RecordingState): boolean => {
    return state.moduleStatus === ModuleStatus.PAUSED;
};

const isModuleRecording = (state: RecordingState): boolean => {
    return state.moduleStatus === ModuleStatus.RECORDING;
};

const isModuleBlocked = (state: RecordingState): boolean => {
    return state.moduleStatus === ModuleStatus.BLOCKED;
};

const isModuleStopped = (state: RecordingState): boolean => {
    return state.moduleStatus === ModuleStatus.STOPPED;
};

const isAwaitingPermission = (state: RecordingState): boolean => {
    return state.moduleStatus === ModuleStatus.AWAITING_MIC_PERMISSION;
};

const isRecording = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.RECORDING;
};

const isRecordingCancelled = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.RECORDING_CANCELLED;
};

const isRecordingPendingRedo = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.PENDING_REDO;
};

const isSavingOngoing = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.SAVING
        || state.recorderStatus === RecorderStatus.SAVING_WAITING_FOR_WORKSPACE
        || state.recorderStatus === RecorderStatus.SAVING_WAITING_FOR_PARTY
        || state.recorderStatus === RecorderStatus.AWAITING_FILENAME_AND_WORKSPACE
        || state.recorderStatus === RecorderStatus.AWAITING_FILENAME;
};

const isSavingFailed = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.SAVING_FAILED;
};

const isUninitialized = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.UNINITIALIZED;
};

const isWaitingForFilenameAndWorkspace = (state: RecordingState): boolean => {
    return state.recorderStatus === RecorderStatus.AWAITING_FILENAME_AND_WORKSPACE;
};

const isOriginalWorkspace = (state: RootState): boolean => {
    const { workspaceId } = state.recordingHeader;
    return state.context.workspaceId === workspaceId;
};

const isOriginalContext = (state: RootState): boolean => {
    const { partyState: { partyId }, workspaceId } = state.recordingHeader;

    if (workspaceId === 'plan_assessment_run_feedback') {
        return isOriginalWorkspace(state);
    }

    return state.context.partyId === partyId
        && isOriginalWorkspace(state);
};

const isPendingUploadOrAwaitingCompletion = (state: RecordingState) => {
    return isSavingOngoing(state) && !!state.recordedAudio.recordedBlob;
};

const isTranscodingOngoing = (state: RecordingState) => {
    return isSavingOngoing(state)
        && !state.recordedAudio.recordedBlob;
};

const isEnacted = (state: RecordingState = initialState): boolean => {
    return state.partyState.partyId > 0
        || (state.workspaceId === 'plan_assessment_run_feedback' && state.assessmentRunId > 0);
};

export const recordingService = {
    shouldShowRecordingHeader(rootState: RootState): boolean {
        if (rootState) {
            if (rootState.context && rootState.context.recordingContextValues) {
                return true;
            }

            if (isEnacted(rootState.recordingHeader)) {
                return true;
            }
        }
        return false;
    },
    isStatusRecording(state: RecordingState): boolean {
        return isRecording(state);
    },
    shouldShowRecorder(state: RecordingState = initialState): boolean {
        return isEnacted(state);
    },
    getRecorderIntervalId(state: RecordingState): number {
        return state.recorderIntervalId;
    },
    getRecordedTime(state: RecordingState): number {
        return state.recordedTime;
    },
    getCandidateName(state: RecordingState = initialState): string {
        return state.partyState.partyName;
    },
    getWorkspaceId(state: RecordingState): string {
        return state.workspaceId;
    },
    shouldIncrementRecordingTime(state: RecordingState): boolean {
        return isModuleRecording(state);
    },
    isMicRecordFlagOn(state: RecordingState): boolean {
        return isModuleRecording(state)
            || isAwaitingPermission(state)
            || isModulePaused(state)
            || isModuleBlocked(state);
    },
    shouldShowPauseButton(state: RecordingState): boolean {
        return isModuleRecording(state);
    },
    shouldShowResumeButton(state: RecordingState): boolean {
        return isModulePaused(state);
    },
    isPaused(state: RecordingState): boolean {
        return isModulePaused(state);
    },
    shouldShowSavingButton(state: RecordingState): boolean {
        return isSavingOngoing(state);
    },
    isOriginalRecordingContext(state: RootState): boolean {
        return isOriginalContext(state);
    },
    shouldSwitchWorkspace(state: RootState): boolean {
        const { workspaceId } = state.recordingHeader;
        return state.context.workspaceId !== workspaceId;
    },
    loadedWorkspaceDuringTranscoding(state: RecordingState): boolean {
        return isTranscodingOngoing(state)
            && state.recorderStatus === RecorderStatus.SAVING;
    },
    existsTranscodingWaitingForFeatureInWorkspace(rootState: RootState, loadedFeatureId: string): boolean {
        const state = rootState.recordingHeader;
        const { featureId } = state;
        return isTranscodingOngoing(state)
            && loadedFeatureId === featureId
            && isOriginalWorkspace(rootState);
    },
    existsFilenameModalWaitingForFeatureInWorkspace(rootState: RootState, loadedFeatureId: string): boolean {
        const state = rootState.recordingHeader;
        const { featureId } = state;
        return isWaitingForFilenameAndWorkspace(state)
            && loadedFeatureId === featureId
            && isOriginalWorkspace(rootState);
    },
    existsPendingUploadWaitingForFeature(state: RecordingState, loadedFeatureId: string): boolean {
        const { featureId } = state;
        return isPendingUploadOrAwaitingCompletion(state) && loadedFeatureId === featureId;
    },
    shouldNotifyAudioUploadCompletion(state: RecordingState): boolean {
        return isPendingUploadOrAwaitingCompletion(state);
    },
    isWaitingForFilenameAndWorkspace(state: RecordingState): boolean {
        return isWaitingForFilenameAndWorkspace(state);
    },
    shouldSavingButtonBeDisabled(state: RecordingState): boolean {
        return isSavingOngoing(state)
            || isRecordingCancelled(state)
            || isRecordingPendingRedo(state);
    },
    shouldResumeButtonBeDisabled(state: RecordingState): boolean {
        return isSavingOngoing(state)
            || isSavingFailed(state)
            || isRecordingCancelled(state)
            || isRecordingPendingRedo(state);
    },
    shouldRedoButtonBeDisabled(state: RecordingState): boolean {
        return isSavingOngoing(state)
            || isSavingFailed(state)
            || isRecordingCancelled(state)
            || isRecordingPendingRedo(state)
            || isUninitialized(state)
            || isAwaitingPermission(state);
    },
    shouldCancelButtonBeDisabled(state: RecordingState): boolean {
        return isSavingOngoing(state)
            || isRecordingCancelled(state)
            || isRecordingPendingRedo(state)
            || isAwaitingPermission(state);
    },
    shouldSaveButtonBeDisabled(state: RecordingState): boolean {
        return isSavingOngoing(state)
            || isRecordingCancelled(state)
            || isRecordingPendingRedo(state)
            || isUninitialized(state)
            || isAwaitingPermission(state);
    },
    shouldPauseButtonBeDisabled(state: RecordingState): boolean {
        return isSavingOngoing(state)
            || isSavingFailed(state)
            || isRecordingCancelled(state)
            || isRecordingPendingRedo(state)
            || isUninitialized(state);
    },

    getStatusCaption(state: RecordingState): string {
        if (isRecording(state)) {
            return RecordStatusCaption.RECORDING;
        }
        if (isSavingOngoing(state)) {
            return RecordStatusCaption.SAVING;
        }
        if (isModulePaused(state) || isSavingFailed(state)) {
            return RecordStatusCaption.PAUSED;
        }
        if (isRecordingCancelled(state)) {
            return RecordStatusCaption.CANCELLING;
        }
        if (isRecordingPendingRedo(state)) {
            return RecordStatusCaption.CANCELLING;
        }
        return RecordStatusCaption.DEFAULT_STATE;
    },
    shouldRetrySaving(state: RecordingState): boolean {
        return isSavingFailed(state);
    },
    getCommittedBlob(state: RecordingState): any {
        return isModuleStopped(state) ? state.recordedAudio.recordedBlob : null;
    },
    getCommittedFilename(state: RecordingState): string {
        return isModuleStopped(state) ? state.recordedAudio.filename : null;
    },
    shouldCloseCancelledRecording(state: RecordingState): boolean {
        return isRecordingCancelled(state);
    },
    shouldRestartRecording(state: RecordingState): boolean {
        return isRecordingPendingRedo(state);
    },
};
